import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  MenuItem,
  FormControl,
  Select,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Card,
  CardContent,
  Modal,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocalGasStation as FuelIcon,
  Build as RepairIcon,
  TireRepair as TireIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  Assessment as AssessmentIcon,
  Visibility,
  VisibilityOff,
  CalendarToday,
  Speed,
  Description,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  CloudDownload as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ListAlt as ListAltIcon,
  Print as PrintIcon,
} from "@mui/icons-material";

import {
  getExpenseList,
  deleteExpense,
  getExpenseSummary,
  getExpenseDetails,
} from "../../../Services/Services";
import { T } from "../../../theme";
import { useDebounce } from "../../../Hooks/useDebounce";
import {fetchAndPrintExpense} from "./expensePrintPDF";
// ── Shared Style Fragments ─────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(amount || 0);

// ── Expense Config ─────────────────────────────────────────────────
const EXPENSE_TYPE_CONFIG = {
  diesel: {
    label: "Diesel",
    color: "#d97706",
    bg: "#fffbeb",
    icon: <FuelIcon />,
  },
  tyre: {
    label: "Tyre",
    color: "#0891b2",
    bg: "#ecfeff",
    icon: <TireIcon />,
  },
  repair: {
    label: "Repair",
    color: "#dc2626",
    bg: "#fef2f2",
    icon: <RepairIcon />,
  },
  service: {
    label: "Service",
    color: "#7c3aed",
    bg: "#f5f3ff",
    icon: <RepairIcon />,
  },
  rto: {
    label: "RTO",
    color: "#64748b",
    bg: "#f8fafc",
    icon: <ReceiptIcon />,
  },
  insurance: {
    label: "Insurance",
    color: "#3b82f6",
    bg: "#eff6ff",
    icon: <ReceiptIcon />,
  },
  tax: {
    label: "Tax",
    color: "#64748b",
    bg: "#f8fafc",
    icon: <MoneyIcon />,
  },
  toll_tax: {
    label: "Toll Tax",
    color: "#0f766e",
    bg: "#f0fdfa",
    icon: <ReceiptIcon />,
  },
  food: {
    label: "Food",
    color: "#059669",
    bg: "#ecfdf5",
    icon: <MoneyIcon />,
  },
  other: {
    label: "Other",
    color: "#64748b",
    bg: "#f8fafc",
    icon: <MoneyIcon />,
  },
};

// ── URL Helpers ────────────────────────────────────────────────────
const resolveUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("//")) return window.location.protocol + s;
  const base = (
    process.env.REACT_APP_API_URL || "http://localhost:8000"
  ).replace(/\/$/, "");
  let path = s.startsWith("/") ? s : "/" + s;
  if (!path.startsWith("/media/")) path = "/media" + path;
  return base + path;
};

const isImageUrl = (url) =>
  url ? /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url) : false;

// Groups documents by line item instead of flattening the whole voucher,
// since each line item can carry its own service/expense bill now.
const extractVoucherDocGroups = (voucherData) => {
  const items = voucherData?.line_items || [];
  const groups = [];
  items.forEach((item, idx) => {
    const typeInfo =
      EXPENSE_TYPE_CONFIG[item.expense_type] || EXPENSE_TYPE_CONFIG.other;
    const docs = [];
    const svcUrl = resolveUrl(item.service_bill_url);
    const expUrl = resolveUrl(item.expense_bill_url);
    if (svcUrl)
      docs.push({
        label: "Service Bill",
        url: svcUrl,
        isImage: isImageUrl(svcUrl),
      });
    if (expUrl)
      docs.push({
        label: "Expense Bill",
        url: expUrl,
        isImage: isImageUrl(expUrl),
      });
    if (docs.length > 0) {
      groups.push({
        itemLabel: `${typeInfo.label} #${idx + 1}`,
        itemColor: typeInfo.color,
        docs,
      });
    }
  });
  return groups;
};

// ── Dialog Header ──────────────────────────────────────────────────
const DialogHeader = ({ title, subtitle, onClose, extra }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: T.white,
          fontSize: "0.88rem",
          letterSpacing: "0.01em",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.68rem" }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      {extra}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: "rgba(255,255,255,0.8)",
          p: 0.5,
          ...sq,
          "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
        }}
      >
        <CloseIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Box>
  </Box>
);

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete = false,
  isEdit = false,
  isView = false,
  isPurple = false,
  color,
  onClick,
}) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : isPurple
          ? "#8b5cf6"
          : color || T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : isPurple
          ? "rgba(139,92,246,0.08)"
          : T.primaryBg;

  const border = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : isPurple
          ? "rgba(139,92,246,0.2)"
          : T.border;

  const hoverBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : isPurple
          ? "rgba(139,92,246,0.15)"
          : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color: c,
          bgcolor: bg,
          border: `1px solid ${border}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hoverBg,
            borderColor: c,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, subtext, color, icon: Icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      ...sq,
      position: "relative",
      overflow: "hidden",
      bgcolor: `${color}12`,
      border: `1px solid ${color}40`,
      boxShadow: "none",
      transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 4px 15px ${color}25`,
        borderColor: color,
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: -15,
        right: -15,
        width: 60,
        height: 60,
        borderRadius: "50%",
        bgcolor: `${color}15`,
        zIndex: 0,
      }}
    />
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: T.textSub,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "6px",
            bgcolor: `${color}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 14, color }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: "1.1rem",
          fontWeight: 800,
          color: T.text,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      {subtext && (
        <Typography
          sx={{
            fontSize: "0.6rem",
            color: T.textMuted,
            mt: 0.5,
            fontWeight: 600,
          }}
        >
          {subtext}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ── DeletePopover — now shows item count + voucher total, not a single expense_type/amount ──
const DeletePopover = ({ anchorEl, onClose, onConfirm, voucher }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 280 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.redBg,
            border: `1.5px solid ${T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Delete this voucher?
      </Typography>
      {voucher && (
        <Box
          sx={{
            p: 1,
            mb: 1,
            bgcolor: T.redBg,
            border: `1px solid ${T.redBorder}`,
            ...sq,
          }}
        >
          <Typography sx={{ fontSize: "0.65rem", color: T.text }}>
            <strong>Vehicle:</strong> {voucher.vehicle_number}
          </Typography>
          <Typography sx={{ fontSize: "0.65rem", color: T.text }}>
            <strong>Items:</strong> {voucher.line_item_count ?? "—"} expense
            {voucher.line_item_count === 1 ? "" : "s"}
          </Typography>
          <Typography sx={{ fontSize: "0.65rem", color: T.text }}>
            <strong>Total:</strong> {formatCurrency(voucher.total_amount)}
          </Typography>
        </Box>
      )}
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        This will remove all expense entries under this voucher. This action
        cannot be undone.
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...dangerBtn, height: 30, fontSize: "0.7rem" }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── ImagePreviewDialog ─────────────────────────────────────────────
const ImagePreviewDialog = ({ open, onClose, imageUrl, title }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    PaperProps={{
      sx: {
        ...sq,
        border: `1.5px solid ${T.border}`,
        boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
        overflow: "hidden",
      },
    }}
  >
    <DialogHeader title={title} onClose={onClose} />
    <DialogContent
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
    >
      <img
        src={imageUrl}
        alt={title}
        style={{
          maxWidth: "100%",
          maxHeight: "70vh",
          objectFit: "contain",
          borderRadius: 4,
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3C/svg%3E";
        }}
      />
    </DialogContent>
    <DialogActions
      sx={{
        px: 2.5,
        py: 1.5,
        bgcolor: T.white,
        borderTop: `1px solid ${T.border}`,
      }}
    >
      <Button
        size="small"
        startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
        onClick={() => window.open(imageUrl, "_blank")}
        sx={{ ...ghostBtn, height: 30, px: 2 }}
      >
        Open in New Tab
      </Button>
      <Button
        size="small"
        onClick={onClose}
        sx={{ ...ghostBtn, height: 30, px: 2 }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

// ── DocumentCard ───────────────────────────────────────────────────
const DocumentCard = ({ doc, onPreview, onDownload, downloadStatus }) => {
  const [thumbError, setThumbError] = useState(false);
  const ext = doc.url.split(".").pop()?.split("?")[0]?.toLowerCase() || "";
  const isPdf = ext === "pdf";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        ...sq,
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        height: "100%",
        transition: "background 0.2s, box-shadow 0.2s",
        "&:hover": {
          bgcolor: T.primaryLight,
          boxShadow: `0 2px 8px rgba(79,70,229,0.1)`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {doc.isImage && !thumbError ? (
          <Box
            sx={{
              width: 56,
              height: 56,
              ...sq,
              overflow: "hidden",
              border: `1.5px solid ${T.border}`,
              flexShrink: 0,
            }}
          >
            <img
              src={doc.url}
              alt={doc.label}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setThumbError(true)}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 56,
              height: 56,
              ...sq,
              bgcolor: isPdf ? T.redBg : T.primaryBg,
              border: `1.5px solid ${isPdf ? T.redBorder : T.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isPdf ? (
              <PdfIcon sx={{ fontSize: 28, color: T.red }} />
            ) : thumbError ? (
              <ImageIcon sx={{ fontSize: 28, color: T.primary }} />
            ) : (
              <FileIcon sx={{ fontSize: 28, color: T.primary }} />
            )}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: T.text,
              lineHeight: 1.3,
              mb: 0.4,
            }}
          >
            {doc.label}
          </Typography>
          <Chip
            label={
              isPdf
                ? "PDF"
                : doc.isImage
                  ? "Image"
                  : ext.toUpperCase() || "File"
            }
            size="small"
            sx={{
              height: 18,
              fontSize: "0.58rem",
              ...sq,
              bgcolor: isPdf ? T.redBg : T.primaryBg,
              color: isPdf ? T.red : T.primary,
              border: `1px solid ${isPdf ? T.redBorder : T.border}`,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>
      </Box>
      <Stack direction="row" spacing={0.75}>
        <Button
          size="small"
          fullWidth
          startIcon={<Visibility sx={{ fontSize: 13 }} />}
          onClick={() => onPreview(doc)}
          sx={{ ...ghostBtn, height: 28, fontSize: "0.65rem" }}
        >
          View
        </Button>
        <Button
          size="small"
          fullWidth
          startIcon={
            downloadStatus === "downloading" ? (
              <CircularProgress size={11} sx={{ color: T.primary }} />
            ) : downloadStatus === "done" ? (
              <CheckCircleIcon sx={{ fontSize: 13, color: "#10b981" }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 13 }} />
            )
          }
          onClick={() => onDownload(doc)}
          disabled={downloadStatus === "downloading"}
          sx={{ ...ghostBtn, height: 28, fontSize: "0.65rem" }}
        >
          {downloadStatus === "downloading"
            ? "..."
            : downloadStatus === "done"
              ? "Saved"
              : "Download"}
        </Button>
      </Stack>
    </Paper>
  );
};

// ── ViewExpenseDocumentsDialog — now groups by line item ────────────
const ViewExpenseDocumentsDialog = ({
  open,
  onClose,
  expenseId,
  expenseLabel,
}) => {
  const [docGroups, setDocGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState({});

  const fetchDocuments = async () => {
    if (!expenseId) return;
    setLoading(true);
    setError(null);
    setDownloadStatus({});
    try {
      const response = await getExpenseDetails(expenseId);
      const data = response?.data ?? response;
      const groups = extractVoucherDocGroups(data);
      setDocGroups(groups);
      if (groups.length === 0)
        setError("No documents uploaded for this voucher.");
    } catch {
      setError("Failed to load documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && expenseId) fetchDocuments();
    if (!open) {
      setDocGroups([]);
      setError(null);
      setPreviewOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expenseId]);

  const handlePreview = (doc) => {
    if (doc.isImage) {
      setPreviewDoc(doc);
      setPreviewOpen(true);
    } else window.open(doc.url, "_blank");
  };

  const handleDownload = async (doc, groupLabel) => {
    const key = `${groupLabel}-${doc.label}`;
    setDownloadStatus((p) => ({ ...p, [key]: "downloading" }));
    try {
      const res = await fetch(doc.url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext =
        doc.url.split(".").pop()?.split("?")[0]?.toLowerCase() || "bin";
      const filename = `${groupLabel.replace(/\s+/g, "_")}_${doc.label.replace(/\s+/g, "_")}_${expenseLabel || "expense"}.${ext}`;
      const blobUrl = window.URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), {
        href: blobUrl,
        download: filename,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      setDownloadStatus((p) => ({ ...p, [key]: "done" }));
      setTimeout(
        () => setDownloadStatus((p) => ({ ...p, [key]: "idle" })),
        3000,
      );
    } catch {
      window.open(doc.url, "_blank");
      setDownloadStatus((p) => ({ ...p, [key]: "idle" }));
    }
  };

  const totalDocs = docGroups.reduce((sum, g) => sum + g.docs.length, 0);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Vehicle Expense Documents"
          subtitle={expenseLabel}
          onClose={onClose}
          extra={
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={fetchDocuments}
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  p: 0.5,
                  ...sq,
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: T.white,
                  },
                }}
              >
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          }
        />
        <DialogContent
          sx={{
            p: 2,
            minHeight: 280,
            maxHeight: "70vh",
            overflow: "auto",
            bgcolor: T.white,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={36} />
              <Typography
                sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}
              >
                Loading documents…
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Description sx={{ fontSize: 48, color: T.border, mb: 1 }} />
              <Typography
                sx={{
                  color: T.red,
                  fontSize: "0.8rem",
                  mb: 1.5,
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
              <Button
                size="small"
                onClick={fetchDocuments}
                sx={{ ...ghostBtn, height: 30, px: 2 }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              {docGroups.map((group, gIdx) => (
                <Box key={gIdx}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 14,
                        bgcolor: group.itemColor,
                        borderRadius: "4px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        color: T.text,
                      }}
                    >
                      {group.itemLabel}
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    {group.docs.map((doc, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <DocumentCard
                          doc={doc}
                          onPreview={handlePreview}
                          onDownload={(d) => handleDownload(d, group.itemLabel)}
                          downloadStatus={
                            downloadStatus[`${group.itemLabel}-${doc.label}`] ||
                            "idle"
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        {!loading && !error && totalDocs > 0 && (
          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: T.white,
              borderTop: `1px solid ${T.border}`,
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{ fontSize: "0.65rem", color: T.textMuted, fontWeight: 500 }}
            >
              {totalDocs} document{totalDocs !== 1 ? "s" : ""} across{" "}
              {docGroups.length} expense{docGroups.length !== 1 ? "s" : ""}
            </Typography>
            <Button
              size="small"
              onClick={onClose}
              sx={{ ...ghostBtn, height: 30, px: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <ImagePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={previewDoc?.url || ""}
        title={`${previewDoc?.label || ""} — ${expenseLabel || ""}`}
      />
    </>
  );
};

// ── LineItemsPreview — shared by desktop expandable row & mobile card ──
const LineItemsPreview = ({ voucherId }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getExpenseDetails(voucherId);
        const data = response?.data ?? response;
        if (!cancelled) setItems(data?.line_items || []);
      } catch {
        if (!cancelled) setError("Failed to load items");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [voucherId]);

  if (loading) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, px: 1 }}
      >
        <CircularProgress size={14} sx={{ color: T.primary }} />
        <Typography sx={{ fontSize: "0.68rem", color: T.textSub }}>
          Loading expenses…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ fontSize: "0.68rem", color: T.red, py: 1.5, px: 1 }}>
        {error}
      </Typography>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Typography
        sx={{ fontSize: "0.68rem", color: T.textMuted, py: 1.5, px: 1 }}
      >
        No line items on this voucher.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75} sx={{ py: 1 }}>
      {items.map((item, idx) => {
        const tc =
          EXPENSE_TYPE_CONFIG[item.expense_type] || EXPENSE_TYPE_CONFIG.other;
        const hasDocs = !!(item.service_bill_url || item.expense_bill_url);
        return (
          <Box
            key={item.line_item_id || idx}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 0.75,
              bgcolor: T.white,
              border: `1px solid ${T.border}`,
              ...sq,
            }}
          >
            <Chip
              icon={React.cloneElement(tc.icon, {
                sx: {
                  fontSize: "11px !important",
                  color: `${tc.color} !important`,
                },
              })}
              label={tc.label}
              size="small"
              sx={{
                bgcolor: tc.bg,
                color: tc.color,
                ...sq,
                fontSize: "0.6rem",
                height: 18,
                border: `1px solid ${tc.color}22`,
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: T.border }}
            />

            <Typography
              sx={{
                fontSize: "0.68rem",
                color: T.textSub,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {item.vendor_name || "—"}
            </Typography>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: T.border }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Speed sx={{ fontSize: 11, color: T.textMuted }} />
              <Typography sx={{ fontSize: "0.65rem", color: T.textMuted }}>
                {item.km_reading ? `${item.km_reading} km` : "—"}
              </Typography>
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: T.border }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <ReceiptIcon sx={{ fontSize: 11, color: "#7c3aed" }} />
              <Typography
                sx={{ fontSize: "0.65rem", color: T.textSub, fontWeight: 500 }}
              >
                #{item.receipt_no || "—"}
              </Typography>
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: T.border }}
            />

            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: T.text,
              }}
            >
              {formatCurrency(item.amount)}
            </Typography>

            {hasDocs ? (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: T.border }}
                />
                <Tooltip title="Has attached documents">
                  <Description sx={{ fontSize: 13, color: "#10b981" }} />
                </Tooltip>
              </>
            ) : (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: T.border }}
                />
                <Box sx={{ width: 13 }} />
              </>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

// ── MobileExpenseCard — vehicle/date/total at voucher level, line items on expand ──
const MobileExpenseCard = ({
  voucher,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onViewDocuments,
  onPrint,
}) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid ${expanded ? T.primary : T.border}`,
      bgcolor: T.white,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: expanded ? `0 2px 12px rgba(79,70,229,0.1)` : "none",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
      {/* Vehicle icon block */}
      <Box
        sx={{
          width: 60,
          flexShrink: 0,
          bgcolor: expanded ? T.primary : T.primaryBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
          transition: "background-color 0.2s",
          gap: 0.25,
        }}
      >
        <CarIcon sx={{ fontSize: 20, color: expanded ? T.white : T.primary }} />
        <Typography
          sx={{
            fontSize: "0.52rem",
            fontWeight: 700,
            color: expanded ? T.white : T.primary,
            letterSpacing: "0.02em",
          }}
        >
          {voucher.line_item_count ?? 0} ITEM
          {voucher.line_item_count === 1 ? "" : "S"}
        </Typography>
      </Box>

      {/* Info */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 1.5,
          py: 1,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: T.text,
            lineHeight: 1.2,
            mb: 0.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {voucher.vehicle_number || "—"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
          <Typography sx={{ fontSize: "0.65rem", color: T.textSub }}>
            {formatDate(voucher.expense_date)}
          </Typography>
        </Box>
        {voucher.remarks && (
          <Typography
            sx={{
              fontSize: "0.62rem",
              color: T.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: 0.25,
              textAlign: "left",
            }}
          >
            {voucher.remarks}
          </Typography>
        )}
      </Box>

      {/* Amount + Toggle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          px: 1.2,
          py: 1,
          minWidth: 100,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: T.red }}>
          {formatCurrency(voucher.total_amount)}
        </Typography>
        <Button
          size="small"
          onClick={onToggle}
          startIcon={
            expanded ? (
              <VisibilityOff sx={{ fontSize: 12 }} />
            ) : (
              <Visibility sx={{ fontSize: 12 }} />
            )
          }
          sx={{
            ...ghostBtn,
            height: 26,
            fontSize: "0.62rem",
            px: 1,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          {expanded ? "Hide" : "Details"}
        </Button>
      </Box>
    </Box>

    {expanded && (
      <Box
        sx={{
          bgcolor: T.white,
          borderTop: `1.5px solid ${T.border}`,
          p: 1.5,
        }}
      >
        <LineItemsPreview voucherId={voucher.expense_id} />
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px dashed ${T.border}`,
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
            onClick={() => onPrint(voucher)}
            sx={{ ...ghostBtn, height: 30, px: 1.5 }}
          >
            Print
          </Button>
          <Button
            size="small"
            startIcon={<Description sx={{ fontSize: 13 }} />}
            onClick={() => onViewDocuments(voucher)}
            sx={{ ...ghostBtn, height: 30, px: 1.5 }}
          >
            Documents
          </Button>
          <Button
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 13 }} />}
            onClick={() => onEdit(voucher)}
            sx={{ ...ghostBtn, height: 30, px: 1.5 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
            onClick={(e) => onDelete(e, voucher)}
            sx={{ ...dangerBtn, height: 30, px: 1.5 }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    )}
  </Paper>
);

// ── Pagination Sx ──────────────────────────────────────────────────
const paginationSx = {
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  mt: 1,
  ...sq,
  "& .MuiTablePagination-toolbar": {
    minHeight: 44,
    flexWrap: "wrap",
    gap: 0.5,
    px: 1,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.68rem",
    margin: 0,
    color: T.textSub,
    fontWeight: 500,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.68rem",
    ...sq,
    border: `1px solid ${T.border}`,
    bgcolor: T.white,
    px: "6px",
  },
  "& .MuiTablePagination-actions button": { color: T.primary },
};

// ── Main Component ─────────────────────────────────────────────────
export default function ExpenseList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [expenses, setExpenses] = useState([]); // now an array of VOUCHERS
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // filters vouchers containing this line item type
  const [filterVehicle, setFilterVehicle] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState([]);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null); // mobile
  const [expandedRowId, setExpandedRowId] = useState(null); // desktop table
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [selectedExpenseForDocs, setSelectedExpenseForDocs] = useState(null);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const handleRowToggle = (id) =>
    setExpandedRowId((p) => (p === id ? null : id));

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExpenseList(page + 1, {
        search: debouncedSearch,
        expense_type: filterType !== "all" ? filterType : "",
        vehicle_number: filterVehicle !== "all" ? filterVehicle : "",
      });
      if (res?.results) {
        setExpenses(res.results);
        setTotalCount(res.count || 0);
      } else if (res?.data?.results) {
        setExpenses(res.data.results);
        setTotalCount(res.data.count || 0);
      } else if (Array.isArray(res)) {
        setExpenses(res);
        setTotalCount(res.length);
      } else {
        setExpenses([]);
        setTotalCount(0);
      }
    } catch {
      setExpenses([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filterType, filterVehicle]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getExpenseSummary();
      if (Array.isArray(res)) setSummary(res);
    } catch {}
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await getExpenseList(1, {});
      if (res?.results) {
        setVehicles([
          ...new Set(res.results.map((i) => i.vehicle_number).filter(Boolean)),
        ]);
      }
    } catch {}
  }, []);

  useEffect(() => {
    setPage(0);
    setExpandedCardId(null);
    setExpandedRowId(null);
  }, [debouncedSearch, filterType, filterVehicle]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    fetchSummary();
    fetchVehicles();
  }, [fetchSummary, fetchVehicles]);

  const handleDeleteClick = (e, voucher) => {
    setDeleteAnchorEl(e.currentTarget);
    setSelectedExpense(voucher);
  };

  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
    setSelectedExpense(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedExpense) return;
    try {
      await deleteExpense(selectedExpense.expense_id);
      fetchExpenses();
      fetchSummary();
    } catch {}
    handleDeleteClose();
  };

  const handleEdit = (voucher) =>
    navigate(`/dashboard/ExpensesForm/${voucher.expense_id}`);

  const handleAdd = () => navigate("/dashboard/ExpensesForm");

  const handleViewDocuments = (voucher) => {
    setSelectedExpenseForDocs(voucher);
    setDocsDialogOpen(true);
  };

  const handlePrint = (voucher) => {
    fetchAndPrintExpense(voucher.expense_id);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterVehicle("all");
    setPage(0);
  };

  const activeFilterCount = [
    searchTerm,
    filterType !== "all" ? filterType : "",
    filterVehicle !== "all" ? filterVehicle : "",
  ].filter(Boolean).length;

  // Stat cards now derive from the vehicle-wise summary (global, not just the
  // current page), which already breaks amounts down by type server-side.
  const totalExpenseAmount = summary.reduce(
    (s, v) => s + (v.total_expenses || 0),
    0,
  );
  const totalExpenseCount = summary.reduce(
    (s, v) => s + (v.expense_count || 0),
    0,
  );
  const totalDieselCost = summary.reduce((s, v) => s + (v.diesel_cost || 0), 0);
  const totalServiceCost = summary.reduce(
    (s, v) => s + (v.service_cost || 0) + (v.repair_cost || 0),
    0,
  );
  const totalOtherCost =
    totalExpenseAmount - totalDieselCost - totalServiceCost;

  if (loading && expenses.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: T.primary }} size={36} />
        <Typography
          sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}
        >
          Loading expenses…
        </Typography>
      </Box>
    );
  }

  const selectSx = {
    ...sq,
    bgcolor: T.white,
    fontSize: "0.72rem",
    height: 34,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  };

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stats ── */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        {[
          {
            title: "Total Expenses",
            value: formatCurrency(totalExpenseAmount),
            sub: `${totalExpenseCount} records`,
            color: T.primary,
            icon: ReceiptIcon,
          },
          {
            title: "Diesel Cost",
            value: formatCurrency(totalDieselCost),
            sub: "—",
            color: "#d97706",
            icon: FuelIcon,
          },
          {
            title: "Service Cost",
            value: formatCurrency(totalServiceCost),
            sub: "—",
            color: "#7c3aed",
            icon: RepairIcon,
          },
          {
            title: "Other Costs",
            value: formatCurrency(totalOtherCost),
            sub: "—",
            color: "#10b981",
            icon: MoneyIcon,
          },
        ].map(({ title, value, sub, color, icon }) => (
          <Grid item xs={6} sm={3} key={title}>
            <StatCard
              title={title}
              value={value}
              subtext={sub}
              color={color}
              icon={icon}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={4} md={3.5}>
            <TextField
              fullWidth
              placeholder="Search vehicle, vendor…"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: T.primary }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 34,
                  ...sq,
                  bgcolor: T.white,
                },
                "& fieldset": { border: `1.5px solid ${T.border}` },
                "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
                "& .Mui-focused fieldset": {
                  border: `1.5px solid ${T.primary}`,
                },
              }}
            />
          </Grid>

          {/* Type Filter — filters vouchers that CONTAIN this expense type */}
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="all" sx={{ fontSize: "0.72rem" }}>
                  All Types
                </MenuItem>
                {Object.entries(EXPENSE_TYPE_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k} sx={{ fontSize: "0.72rem" }}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Vehicle Filter */}
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <Select
                value={filterVehicle}
                onChange={(e) => setFilterVehicle(e.target.value)}
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="all" sx={{ fontSize: "0.72rem" }}>
                  All Vehicles
                </MenuItem>
                {vehicles.map((v) => (
                  <MenuItem key={v} value={v} sx={{ fontSize: "0.72rem" }}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Clear */}
          {activeFilterCount > 0 && (
            <Grid item xs={12} sm={2} md={1.5}>
              <Button
                fullWidth
                size="small"
                onClick={clearFilters}
                startIcon={<CloseIcon sx={{ fontSize: 13 }} />}
                sx={{ ...dangerBtn, height: 34, fontSize: "0.7rem" }}
              >
                Clear ({activeFilterCount})
              </Button>
            </Grid>
          )}

          {/* Icon Buttons */}
          {[
            {
              tip: "Refresh",
              icon: <RefreshIcon sx={{ fontSize: 15 }} />,
              action: fetchExpenses,
            },
            {
              tip: "Summary",
              icon: <AssessmentIcon sx={{ fontSize: 15 }} />,
              action: () => setSummaryModalOpen(true),
            },
          ].map(({ tip, icon, action }) => (
            <Grid item xs={2} sm={1} md={0.75} key={tip}>
              <Tooltip title={tip} arrow>
                <Button
                  fullWidth
                  size="small"
                  onClick={action}
                  sx={{ ...iconBtn, height: 34 }}
                >
                  {icon}
                </Button>
              </Tooltip>
            </Grid>
          ))}

          <Grid item xs={12} sm={4} md={3}>
            <Stack direction="row" spacing={0.75}>
              <Box
                sx={{
                  height: 34,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  ...sq,
                  bgcolor: T.white,
                  color: T.primary,
                  border: `1.5px solid ${T.border}`,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                }}
              >
                <ReceiptIcon sx={{ fontSize: 15, color: T.primary }} />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: T.primary,
                    lineHeight: 1,
                  }}
                >
                  {totalCount} Vouchers
                </Typography>
              </Box>
              <Button
                onClick={handleAdd}
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                sx={{
                  ...primaryBtn,
                  height: 34,
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                  px: 2,
                }}
              >
                Add Expense
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* ── Table / Mobile ── */}
      {isMobile ? (
        loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : expenses.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No expenses found
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.textSub, mb: 2 }}>
              {searchTerm
                ? "Try a different search term"
                : "Add your first expense"}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ ...primaryBtn, px: 2 }}
            >
              Add Expense
            </Button>
          </Box>
        ) : (
          <>
            {expenses.map((voucher) => (
              <MobileExpenseCard
                key={voucher.expense_id}
                voucher={voucher}
                expanded={expandedCardId === voucher.expense_id}
                onToggle={() => handleCardToggle(voucher.expense_id)}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onViewDocuments={handleViewDocuments}
                onPrint={handlePrint} 
              />
            ))}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => {
                setPage(p);
                setExpandedCardId(null);
              }}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={paginationSx}
            />
          </>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          }}
        >
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 800,
                borderCollapse: "collapse",
                "& td, & th": {
                  py: 0,
                  px: 1,
                  fontSize: "0.75rem",
                  borderBottom: `1px solid #ccc`,
                  lineHeight: 1.2,
                },
                "& th": {
                  fontWeight: 700,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  py: 0.2,
                  fontSize: "0.8rem",
                  letterSpacing: "0.01em",
                  borderBottom: `1px solid #ccc`,
                  lineHeight: 1.2,
                  height: 28,
                },
                "& td": {
                  height: 26,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    { label: "", align: "center", minW: 36 }, // expand toggle
                    {
                      label: "Date",
                      align: "left",
                      sticky: true,
                      left: 36,
                      minW: 100,
                    },
                    {
                      label: "Vehicle",
                      align: "left",
                      sticky: true,
                      left: 136,
                      minW: 110,
                    },
                    { label: "Items", align: "center", minW: 90 },
                    { label: "Remarks", align: "left", minW: 180 },
                    { label: "Total Amount", align: "right", minW: 110 },
                    {
                      label: "Actions",
                      align: "center",
                      sticky: true,
                      right: 0,
                      minW: 110,
                    },
                  ].map(({ label, align, sticky, left, right, minW }, i) => (
                    <TableCell
                      key={label || i}
                      align={align}
                      sx={{
                        minWidth: minW,
                        bgcolor: `${T.headerBg} !important`,
                        color: `${T.headerText} !important`,
                        ...(sticky && {
                          position: "sticky",
                          ...(left !== undefined && { left }),
                          ...(right !== undefined && { right }),
                          zIndex: 12,
                        }),
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <CircularProgress sx={{ color: T.primary }} size={32} />
                    </TableCell>
                  </TableRow>
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <ReceiptIcon
                        sx={{
                          fontSize: 40,
                          color: T.border,
                          mb: 1,
                          display: "block",
                          mx: "auto",
                        }}
                      />
                      <Typography
                        sx={{
                          color: T.textSub,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        No expenses found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((voucher, idx) => {
                    const isEven = idx % 2 === 0;
                    const rowBg = isEven ? T.baseRow : T.altRow;
                    const isRowOpen = expandedRowId === voucher.expense_id;

                    return (
                      <React.Fragment key={voucher.expense_id}>
                        <TableRow
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* Expand toggle */}
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleRowToggle(voucher.expense_id)
                              }
                              sx={{ p: 0.3, color: T.primary }}
                            >
                              {isRowOpen ? (
                                <ExpandLessIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </TableCell>

                          {/* Date */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 36,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: T.text,
                                fontWeight: 500,
                              }}
                            >
                              {formatDate(voucher.expense_date)}
                            </Typography>
                          </TableCell>

                          {/* Vehicle */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 136,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: T.primary,
                              }}
                            >
                              {voucher.vehicle_number}
                            </Typography>
                          </TableCell>

                          {/* Items count badge */}
                          <TableCell align="center">
                            <Chip
                              icon={<ListAltIcon sx={{ fontSize: 12 }} />}
                              label={`${voucher.line_item_count ?? 0} item${voucher.line_item_count === 1 ? "" : "s"}`}
                              size="small"
                              onClick={() =>
                                handleRowToggle(voucher.expense_id)
                              }
                              sx={{
                                bgcolor: T.primaryBg,
                                color: T.primary,
                                ...sq,
                                fontSize: "0.6rem",
                                height: 20,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                cursor: "pointer",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* Remarks (voucher-level) */}
                          <TableCell>
                            <Tooltip title={voucher.remarks || "—"} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: T.text,
                                  maxWidth: 180,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {voucher.remarks || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Total Amount */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: T.red,
                              }}
                            >
                              {formatCurrency(voucher.total_amount)}
                            </Typography>
                          </TableCell>

                          {/* Actions */}
                          <TableCell
                            align="center"
                            sx={{
                              position: "sticky",
                              right: 0,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 5,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="Print"
                                icon={<PrintIcon />}
                                color="#059669"
                                onClick={() => handlePrint(voucher)}
                              />
                              <ActionButton
                                tip="View Documents"
                                icon={<Description />}
                                isPurple
                                onClick={() => handleViewDocuments(voucher)}
                              />
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEdit(voucher)}
                              />
                              <ActionButton
                                tip="Delete"
                                icon={<DeleteIcon />}
                                isDelete
                                onClick={(e) => handleDeleteClick(e, voucher)}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {/* Expanded line items row */}
                        {isRowOpen && (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              sx={{
                                bgcolor: "#fafafa !important",
                                py: "0 !important",
                              }}
                            >
                              <Box sx={{ px: 2 }}>
                                <LineItemsPreview
                                  voucherId={voucher.expense_id}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > 0 && (
            <Box sx={{ borderTop: `1px solid #ccc`, bgcolor: T.white }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => {
                  setPage(p);
                  setExpandedRowId(null);
                }}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  "& .MuiTablePagination-toolbar": { minHeight: 36, px: 2 },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: "0.7rem",
                      color: T.textSub,
                      fontWeight: 500,
                      margin: 0,
                    },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.7rem",
                    ...sq,
                    border: `1px solid #ccc`,
                    bgcolor: T.white,
                    px: "5px",
                  },
                  "& .MuiTablePagination-actions button": { color: T.primary },
                  "& .MuiTablePagination-actions button:disabled": {
                    color: T.textMuted,
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* ── Summary Modal — unchanged, backend summary shape is the same ── */}
      <Modal open={summaryModalOpen} onClose={() => setSummaryModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "95%" : "90%",
            maxWidth: 1200,
            maxHeight: "85vh",
            bgcolor: T.white,
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
              px: 2.5,
              py: 1.75,
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CarIcon sx={{ color: T.white, fontSize: 18 }} />
              <Typography
                sx={{ fontWeight: 700, color: T.white, fontSize: "0.88rem" }}
              >
                Vehicle-wise Expense Summary
              </Typography>
            </Stack>
            <IconButton
              onClick={() => setSummaryModalOpen(false)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.8)",
                p: 0.5,
                ...sq,
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          <Box sx={{ p: 2 }}>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                {
                  label: "Total Vehicles",
                  value: summary.length,
                  color: T.primary,
                },
                {
                  label: "Total Expenses",
                  value: formatCurrency(totalExpenseAmount),
                  color: T.red,
                },
                {
                  label: "Total Records",
                  value: totalExpenseCount,
                  color: "#10b981",
                },
              ].map(({ label, value, color }) => (
                <Grid item xs={4} key={label}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      textAlign: "center",
                      ...sq,
                      border: `1.5px solid ${T.border}`,
                      bgcolor: T.white,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        color: T.textMuted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "1.1rem", fontWeight: 800, color }}
                    >
                      {value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={1.5}>
              {summary.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: T.textSub,
                      fontSize: "0.75rem",
                    }}
                  >
                    No summary data available
                  </Typography>
                </Grid>
              ) : (
                summary.map((vehicle, idx) => {
                  const svcCost =
                    (vehicle.diesel_cost || 0) +
                    (vehicle.repair_cost || 0) +
                    (vehicle.service_cost || 0);
                  const otherCst =
                    (vehicle.total_expenses || 0) -
                    (vehicle.diesel_cost || 0) -
                    svcCost;
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...sq,
                          border: `1.5px solid ${T.border}`,
                          bgcolor: T.white,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            bgcolor: T.primaryLight,
                            borderBottom: `1px solid ${T.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CarIcon sx={{ color: T.primary, fontSize: 15 }} />
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              color: T.primary,
                            }}
                          >
                            {vehicle.vehicle_number}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1.25 }}>
                          <Grid container spacing={0.75}>
                            {[
                              {
                                label: "Total",
                                value: formatCurrency(vehicle.total_expenses),
                                color: T.red,
                              },
                              {
                                label: "Count",
                                value: vehicle.expense_count,
                                color: T.text,
                              },
                              {
                                label: "Diesel",
                                value: formatCurrency(vehicle.diesel_cost),
                                color: "#d97706",
                              },
                              {
                                label: "Service",
                                value: formatCurrency(svcCost),
                                color: "#7c3aed",
                              },
                              {
                                label: "Other",
                                value: formatCurrency(otherCst),
                                color: "#10b981",
                              },
                            ].map(({ label, value, color }) => (
                              <Grid item xs={6} key={label}>
                                <Typography
                                  sx={{
                                    fontSize: "0.58rem",
                                    color: T.textMuted,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    color,
                                  }}
                                >
                                  {value}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })
              )}
            </Grid>
          </Box>
        </Box>
      </Modal>

      {/* ── Delete Popover ── */}
      <DeletePopover
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        voucher={selectedExpense}
      />

      {/* ── Docs Dialog ── */}
      <ViewExpenseDocumentsDialog
        open={docsDialogOpen}
        onClose={() => setDocsDialogOpen(false)}
        expenseId={selectedExpenseForDocs?.expense_id}
        expenseLabel={`${selectedExpenseForDocs?.vehicle_number || ""} — ${formatDate(selectedExpenseForDocs?.expense_date)}`}
      />
    </Box>
  );
}
